import { z } from 'zod';
import { defaultLayer } from '@strength/db-schema';
import { zCompletionStatus, zDataSource, zMetricType, zSetMetricEnteredBy, } from '../../core';
import { schemaToModelChange } from '../../utils/schema';
const zWorkoutLayerSectionAdd = z.object({
    id: z.string(),
    workoutSectionId: z.string(),
    name: z.string(),
    sortOrder: z.number(),
    instruction: z.string().nullish(),
    color: z.string().nullish(),
});
const zWorkoutLayerSectionEdit = z.object({
    id: z.string(),
    workoutSectionId: z.string(),
    name: z.string().nullish(),
    sortOrder: z.number().nullish(),
    instruction: z.string().nullish(),
    color: z.string().nullish(),
    status: zCompletionStatus.nullish(),
});
const zWorkoutLayerSupersetAdd = z.object({
    id: z.string(),
    workoutSectionId: z.string(),
    workoutSupersetId: z.string(),
    sortOrder: z.number(),
});
const zWorkoutLayerSupersetEdit = z.object({
    id: z.string(),
    workoutSupersetId: z.string(),
    sortOrder: z.number().nullish(),
    status: zCompletionStatus.nullish(),
});
const zWorkoutLayerSupersetExerciseAdd = z.object({
    id: z.string(),
    workoutSupersetId: z.string(),
    workoutSupersetExerciseId: z.string(),
    workoutExerciseId: z.string(),
    sortOrder: z.number(),
});
const zWorkoutLayerSupersetExerciseEdit = z.object({
    id: z.string(),
    workoutSupersetExerciseId: z.string(),
    sortOrder: z.number().nullish(),
});
const zWorkoutLayerAlternateGroupAdd = z.object({
    id: z.string(),
    workoutSectionId: z.string(),
    workoutAlternateGroupId: z.string(),
    sortOrder: z.number(),
    selectedExerciseId: z.string(),
});
const zWorkoutLayerAlternateGroupEdit = z.object({
    id: z.string(),
    workoutAlternateGroupId: z.string(),
    sortOrder: z.number().nullish(),
    selectedExerciseId: z.string().nullish(),
});
const zWorkoutLayerAlternateExerciseAdd = z.object({
    id: z.string(),
    workoutAlternateGroupId: z.string(),
    workoutAlternateExerciseId: z.string(),
    workoutExerciseId: z.string(),
    sortOrder: z.number(),
});
const zWorkoutLayerAlternateExerciseEdit = z.object({
    id: z.string(),
    workoutAlternateExerciseId: z.string(),
    sortOrder: z.number().nullish(),
});
const zWorkoutLayerExerciseAdd = z.object({
    id: z.string(),
    workoutExerciseId: z.string(),
    workoutSectionId: z.string(),
    libraryExerciseId: z.string(),
    exerciseName: z.string(),
    instruction: z.string().nullish(),
    sortOrder: z.number(),
    isEachSide: z.boolean().nullish(),
});
const zWorkoutLayerExerciseEdit = z.object({
    id: z.string(),
    workoutExerciseId: z.string(),
    libraryExerciseId: z.string().nullish(),
    exerciseName: z.string().nullish(),
    instruction: z.string().nullish(),
    sortOrder: z.number().nullish(),
    status: zCompletionStatus.nullish(),
    isEachSide: z.boolean().nullish(),
});
const zWorkoutLayerExerciseMetricAdd = z.object({
    id: z.string(),
    workoutExerciseMetricId: z.string(),
    sortOrder: z.number(),
    workoutExerciseId: z.string(),
    metricId: z.string(),
    metricName: z.string(),
    metricType: zMetricType,
    benchmarkGridId: z.uuid().optional(),
});
const zWorkoutLayerSetAdd = z.object({
    id: z.string(),
    workoutSetId: z.string(),
    workoutExerciseId: z.string(),
    sortOrder: z.number(),
    workoutSetGroupId: z.string(),
});
const zWorkoutLayerSetEdit = z.object({
    id: z.string(),
    workoutSetId: z.string(),
    workoutExerciseId: z.string(),
    sortOrder: z.number().nullish(),
    status: zCompletionStatus.nullish(),
});
const zWorkoutLayerSetMetric = z.object({
    id: z.string(),
    workoutSetId: z.string(),
    metricId: z.string(),
    forCollection: z.boolean().nullish(),
    input: z.string().nullish(),
    dataSource: zDataSource.optional(),
    enteredBy: zSetMetricEnteredBy.optional(),
    enteredOn: z.iso.datetime({ offset: true }).optional(),
});
const zWorkoutLayerRemoval = z.object({
    id: z.string(),
    entityId: z.string(),
    entityType: z.enum([
        'section',
        'exercise',
        'exerciseMetric',
        'set',
        'superset',
        'supersetExercise',
        'alternateGroup',
        'alternateExercise',
    ]),
});
export const zWorkoutLayer = z.preprocess((data) => {
    if (data && typeof data === 'object') {
        // handles outdated layer objects created outside of the database
        return {
            ...defaultLayer(),
            ...data,
        };
    }
    return data;
}, z.object({
    sectionAdds: z.array(zWorkoutLayerSectionAdd),
    sectionEdits: z.array(zWorkoutLayerSectionEdit),
    supersetAdds: z.array(zWorkoutLayerSupersetAdd),
    supersetEdits: z.array(zWorkoutLayerSupersetEdit),
    supersetExerciseAdds: z.array(zWorkoutLayerSupersetExerciseAdd),
    supersetExerciseEdits: z.array(zWorkoutLayerSupersetExerciseEdit),
    alternateGroupAdds: z.array(zWorkoutLayerAlternateGroupAdd),
    alternateGroupEdits: z.array(zWorkoutLayerAlternateGroupEdit),
    alternateExerciseAdds: z.array(zWorkoutLayerAlternateExerciseAdd),
    alternateExerciseEdits: z.array(zWorkoutLayerAlternateExerciseEdit),
    exerciseAdds: z.array(zWorkoutLayerExerciseAdd),
    exerciseEdits: z.array(zWorkoutLayerExerciseEdit),
    exerciseMetricAdds: z.array(zWorkoutLayerExerciseMetricAdd),
    setAdds: z.array(zWorkoutLayerSetAdd),
    setEdits: z.array(zWorkoutLayerSetEdit),
    setMetrics: z.array(zWorkoutLayerSetMetric),
    removals: z.array(zWorkoutLayerRemoval),
    // Ensure this always aligns with the database type
}));
// Re-export this type so web has access
export { defaultLayer };
export const zHasWorkoutLayerEdit = z.union([
    schemaToModelChange('BlockDraftWorkoutLayer', z.object({ layer: zWorkoutLayer, name: z.string().nullable() })),
    schemaToModelChange('AthleteDraftWorkoutLayer', z.object({ layer: zWorkoutLayer, name: z.string().nullable() })),
    schemaToModelChange('AthleteActualWorkoutLayer', z.object({ layer: zWorkoutLayer })),
    schemaToModelChange('AthleteModifiedPublishedWorkoutLayer', z.object({ layer: zWorkoutLayer })),
]);
/**
 * Drop delete edits that an earlier edit in the list has already applied.
 *
 * Callers assemble one edit list from several independent WorkoutLayerBuilder runs (for example
 * the tablet edit-exercise save, which combines metric, set and exercise-swap edits). Each
 * builder resolves its deletes against its own unmodified copy of the layer, so the same entity
 * can be deleted more than once across the combined list. `mergeLayerEdits` throws on the
 * redundant delete, which would abandon the whole batch.
 *
 * A create for the same component and id makes any following delete of it valid again, so an
 * intervening create clears the applied-delete state for that id.
 */
export function dedupeLayerEdits(edits) {
    const appliedDeletes = new Set();
    return edits.filter((edit) => {
        const key = `${edit.componentName}:${edit.data.id}`;
        // A create puts the row back, so a later delete of it is no longer redundant
        if (edit.action === 'create') {
            appliedDeletes.delete(key);
            return true;
        }
        if (edit.action !== 'delete') {
            return true;
        }
        if (appliedDeletes.has(key)) {
            return false;
        }
        appliedDeletes.add(key);
        return true;
    });
}
/**
 * Add an edit to a layer object. This creates a copy of the layer, leaving the original intact.
 */
export function mergeLayerEdits(workoutLayer, edits) {
    const layer = JSON.parse(JSON.stringify(workoutLayer));
    edits?.forEach(({ componentName, action, data }) => {
        const components = layer[componentName];
        const existingIndex = components.findIndex((c) => c.id === data.id);
        if (action === 'create') {
            if (existingIndex >= 0) {
                throw new Error(`${componentName}:${data.id} already exists`);
            }
            // typescript can't seem to infer the correct type of `data` even though it is asserted by WorkoutLayerComponentEdit
            components.push(data);
        }
        else if (action === 'update') {
            if (existingIndex < 0) {
                throw new Error(`Could not find ${componentName}:${data.id} to update`);
            }
            components[existingIndex] =
                data;
        }
        else if (action === 'delete') {
            if (existingIndex < 0) {
                throw new Error(`Could not find ${componentName}:${data.id} to delete`);
            }
            components.splice(existingIndex, 1);
        }
    });
    return layer;
}
//# sourceMappingURL=edit.js.map